import { addEntity } from "../data/mutations";
import {
  cloneState,
  type EntityStatus,
  type EntityType,
  type FieldType,
  type SchemaField,
  type SyncrelEntity,
  type SyncrelState,
} from "./types";

const BASE_HEADERS = ["id", "type", "name", "status", "doc", "positionX", "positionY"] as const;
const BASE_HEADER_SET = new Set<string>(BASE_HEADERS);
const ENTITY_TYPES: EntityType[] = ["goal", "actor", "impact", "feature", "note", "feedback", "deliverable"];
const ENTITY_STATUSES: EntityStatus[] = ["active", "planned", "done", "archived"];

export const exportGridCsv = (state: SyncrelState, entityIds: string[] = []): string => {
  const selected = entityIds.length > 0 ? new Set(entityIds) : null;
  const entities = selected ? state.entities.filter((entity) => selected.has(entity.id)) : state.entities;
  const fieldKeys = collectFieldKeys(state, entities);
  const headers = [...BASE_HEADERS, ...fieldKeys];
  const rows = [
    headers,
    ...entities.map((entity) => [
      entity.id,
      entity.type,
      entity.name,
      entity.status,
      entity.doc,
      String(entity.position.x),
      String(entity.position.y),
      ...fieldKeys.map((key) => fieldToCsv(entity.fields[key])),
    ]),
  ];
  return rows.map((row) => row.map(encodeCell).join(",")).join("\n");
};

export const importGridCsv = (state: SyncrelState, csv: string, now = Date.now()): SyncrelState => {
  const rows = parseCsv(csv).filter((row) => row.some((cell) => cell.trim() !== ""));
  if (rows.length <= 1) return state;

  const headers = rows[0].map((header) => header.trim());
  const fieldHeaders = headers.filter((header) => header && !BASE_HEADER_SET.has(header));
  const schemaFields = inferSchemaFields(state, fieldHeaders, rows.slice(1), headers);
  let next = cloneState(state);
  next.schema.fields = mergeSchemaFields(next.schema.fields, schemaFields);

  rows.slice(1).forEach((row, index) => {
    const record = recordFromRow(headers, row);
    const name = record.name?.trim() || "Imported entity";
    const position = {
      x: numberFromCell(record.positionX, 180 + index * 28),
      y: numberFromCell(record.positionY, 160 + index * 28),
    };
    const fields = Object.fromEntries(
      fieldHeaders
        .filter((key) => record[key]?.trim() !== "")
        .map((key) => [key, coerceFieldValue(record[key] ?? "", schemaFields.get(key)?.type)]),
    );
    next = addEntity(next, {
      id: record.id?.trim() || undefined,
      type: parseEntityType(record.type),
      name,
      status: parseEntityStatus(record.status),
      doc: record.doc?.trim() || `# ${name}\n`,
      position,
      fields,
    }, now + index);
  });

  next.workspace.updatedAt = now;
  return next;
};

const collectFieldKeys = (state: SyncrelState, entities: SyncrelEntity[]): string[] => {
  const keys: string[] = [];
  const add = (key: string) => {
    if (!BASE_HEADER_SET.has(key) && !keys.includes(key)) keys.push(key);
  };
  state.schema.fields.forEach((field) => add(field.key));
  entities.forEach((entity) => Object.keys(entity.fields).forEach(add));
  return keys;
};

const encodeCell = (value: string): string => {
  if (!/[",\n\r]/.test(value)) return value;
  return `"${value.replaceAll('"', '""')}"`;
};

const fieldToCsv = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

const parseCsv = (csv: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index];
    const next = csv[index + 1];

    if (quoted) {
      if (char === '"' && next === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"' && cell === "") {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char === "\r") {
      if (next === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }

  row.push(cell);
  rows.push(row);
  return rows;
};

const recordFromRow = (headers: string[], row: string[]): Record<string, string> => Object.fromEntries(
  headers.map((header, index) => [header, row[index] ?? ""]),
);

const parseEntityType = (value: string | undefined): EntityType => {
  const normalized = value?.trim() as EntityType | undefined;
  return normalized && ENTITY_TYPES.includes(normalized) ? normalized : "feature";
};

const parseEntityStatus = (value: string | undefined): EntityStatus => {
  const normalized = value?.trim() as EntityStatus | undefined;
  return normalized && ENTITY_STATUSES.includes(normalized) ? normalized : "planned";
};

const numberFromCell = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const coerceFieldValue = (value: string, type: FieldType | undefined): unknown => {
  const trimmed = value.trim();
  if (type === "boolean") return /^(true|yes|1)$/i.test(trimmed);
  if (type === "number" || type === "currency" || type === "percent" || type === "computed") {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : trimmed;
  }
  if (/^(true|false)$/i.test(trimmed)) return /^true$/i.test(trimmed);
  const parsed = Number(trimmed);
  return trimmed !== "" && Number.isFinite(parsed) ? parsed : value;
};

const inferSchemaFields = (
  state: SyncrelState,
  fieldHeaders: string[],
  rows: string[][],
  headers: string[],
): Map<string, SchemaField> => {
  const existing = new Map(state.schema.fields.map((field) => [field.key, field]));
  const inferred = new Map<string, SchemaField>();
  fieldHeaders.forEach((key) => {
    const existingField = existing.get(key);
    if (existingField) {
      inferred.set(key, existingField);
      return;
    }
    const values = rows.map((row) => row[headers.indexOf(key)] ?? "").filter((value) => value.trim() !== "");
    inferred.set(key, {
      key,
      label: labelFromKey(key),
      type: inferFieldType(values),
    });
  });
  return inferred;
};

const mergeSchemaFields = (fields: SchemaField[], inferred: Map<string, SchemaField>): SchemaField[] => {
  const keys = new Set(fields.map((field) => field.key));
  const additions = Array.from(inferred.values()).filter((field) => !keys.has(field.key));
  return [...fields, ...additions];
};

const inferFieldType = (values: string[]): FieldType => {
  if (values.length > 0 && values.every((value) => /^(true|false|yes|no|0|1)$/i.test(value.trim()))) {
    return "boolean";
  }
  if (values.length > 0 && values.every((value) => Number.isFinite(Number(value)))) return "number";
  return "text";
};

const labelFromKey = (key: string): string => key
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (char) => char.toUpperCase());
